import create from 'zustand';
import {
  IAssignment,
  IClassroom,
  IQuestionAnswer,
  IStudent,
  IUser,
} from '@/components/staffer/Grading/types';
import { ISubmission } from '@/components/interfaces';

export type QuestionsOptions = 'all' | 'answered' | 'unanswered';
export type ShowHideOptions = 'hide' | 'show';
export type StatusOptions = 'all' | 'graded' | 'ungraded';

type Filters = {
  answerKeyFilter: ShowHideOptions;
  autoGradedFilter: ShowHideOptions;
  questionsFilter: QuestionsOptions;
  statusFilter: StatusOptions;
};

interface GradingStoreTypes {
  assignment: IAssignment;
  classroom: IClassroom;
  currentUser: IUser;
  filters: Filters;
  isLoading: boolean;
  isQuestionMode: boolean;
  isTeacher: boolean;
  programId: number;
  questionAnswers: IQuestionAnswer[];
  questionId: number;
  student: IStudent;
  studentViewAssessmentResults: boolean;
  submission: ISubmission;
  submissions: ISubmission[];
  userType: string;
}

interface IQuestionAnswerData {
  id: number;
  score: string;
}

interface UseGradingStoreTypes extends GradingStoreTypes {
  isAssessment: (assignment?: IAssignment) => boolean;
  setFilter: (filter) => void;
  setGradingStore: (nextState: Partial<GradingStoreTypes>) => void;
  showAnswers: () => boolean;
  submissionForStudent: (id: number | string) => ISubmission;
  updateQuestionAnswer: (questionAnswerData: IQuestionAnswerData) => void;
}

const useGradingStore = create<UseGradingStoreTypes>((set, get) => ({
  assignment: null,
  classroom: null,
  currentUser: null,
  filters: {
    answerKeyFilter: 'show',
    autoGradedFilter: 'show',
    questionsFilter: 'all',
    statusFilter: 'all',
  },
  isAssessment: (assignment = null) => (
    (assignment || get().assignment)?.assignment_details?.exercise_type === 'Assessment'
  ),
  isLoading: true,
  isQuestionMode: false,
  isTeacher: false,
  programId: null,
  questionAnswers: null,
  questionId: null,
  setFilter: filter => set(prevState => ({ ...prevState, filters: { ...prevState.filters, ...filter } })),
  setGradingStore: nextState => set(prevState => ({ ...prevState, ...nextState })),
  showAnswers: () => {
    const { isAssessment, studentViewAssessmentResults, userType } = get();

    if (userType === 'Student' && isAssessment()) return studentViewAssessmentResults;

    return true;
  },
  student: null,
  studentViewAssessmentResults: null,
  submission: null,
  submissionForStudent: (studentId) => {
    const { assignment, submission, submissions } = get();
    if (!assignment) return null;

    return submission || submissions.find(sub => sub.assignment_id === assignment.id
      && sub.user_type === 'Student'
      && sub.user_id === studentId);
  },
  submissions: [],
  updateQuestionAnswer: (questionAnswerData: IQuestionAnswerData) => set((prevState) => {
    const updatedQuestionAnswers = prevState.questionAnswers.map(qa => (
      qa.id === questionAnswerData.id
        ? { ...qa, ...questionAnswerData }
        : qa
    ));
    return { questionAnswers: updatedQuestionAnswers };
  }),
  userType: null,
}));

export default useGradingStore;
